import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import type {
  Comment,
  FavoritePostResponse,
  ReactionType,
  StreamContent,
} from "@/lib/humhub-api/types";
import { applyReactionSummaryChange } from "@/lib/utils/reactionSummary";

// ページネーション形式の型定義
interface StreamContentPage {
  results: StreamContent[];
  links: {
    next?: string | null;
  };
  total: number;
  page: number;
  pages: number;
}

interface FavoritePostsPage {
  results: FavoritePostResponse[];
  links: {
    next?: string | null;
  };
  total: number;
  page: number;
  pages: number;
}

interface CommentsData {
  comments: Comment[];
  total: number;
  currentPage: number;
  hasMore: boolean;
}

// キャッシュキーの統一管理
export const cacheKeys = {
  // お気に入り投稿
  favoritePosts: {
    all: ["favoritePosts"] as const,
    list: () => ["favoritePosts", "list"] as const,
  },
  // 通常投稿
  posts: {
    all: ["posts"] as const,
    lists: () => ["posts", "list"] as const,
    list: (groupId: number) => ["posts", "list", groupId] as const,
  },
  // コメント
  comments: {
    all: ["comments"] as const,
    post: (postId: number | string) =>
      ["comments", "post", String(postId)] as const,
  },
} as const;

// 統合リアクション更新関数
export const updateReactionInAllCaches = (
  queryClient: QueryClient,
  params: {
    postObjectId: number;
    postContentId?: number;
    commentId?: number;
    reaction: ReactionType | null;
    groupId?: number;
    targetType?: "post" | "comment";
    currentUserId?: number;
  },
) => {
  const {
    postObjectId,
    commentId,
    reaction,
    groupId,
    targetType = "post",
    currentUserId,
  } = params;

  // 1. ストリーム画面のキャッシュ更新
  if (groupId) {
    updateStreamPostReaction(queryClient, {
      postObjectId,
      commentId,
      reaction,
      groupId,
      targetType,
      currentUserId,
    });
  }

  // 2. お気に入り画面のキャッシュ更新
  updateFavoritePostReaction(queryClient, {
    postObjectId,
    commentId,
    reaction,
    targetType,
    currentUserId,
  });

  // 3. コメントキャッシュの更新（コメントリアクションの場合）
  if (targetType === "comment" && commentId) {
    updateCommentReaction(queryClient, {
      postObjectId,
      commentId,
      reaction,
      currentUserId,
    });
  }
};

// ストリーム画面のリアクション更新
const updateStreamPostReaction = (
  queryClient: QueryClient,
  {
    postObjectId,
    commentId,
    reaction,
    groupId,
    targetType,
    currentUserId,
  }: {
    postObjectId: number;
    commentId?: number;
    reaction: ReactionType | null;
    groupId: number;
    targetType: "post" | "comment";
    currentUserId?: number;
  },
) => {
  const queryKey = cacheKeys.posts.list(groupId);

  queryClient.setQueriesData<InfiniteData<StreamContentPage>>(
    { queryKey },
    (oldData) => {
      if (!oldData?.pages) {
        console.log("❌ No stream data found for groupId:", groupId);
        return oldData;
      }

      return {
        ...oldData,
        pages: oldData.pages.map((page) => ({
          ...page,
          results: page.results.map((post: StreamContent) => {
            if (post.metadata?.object_id === postObjectId) {
              if (targetType === "post") {
                return {
                  ...post,
                  reactions: applyReactionSummaryChange(
                    post.reactions,
                    reaction,
                    currentUserId,
                  ),
                };
              }
              if (targetType === "comment" && commentId) {
                // コメントのリアクション更新
                return {
                  ...post,
                  comments: {
                    ...post.comments,
                    latest: post.comments?.latest?.map((comment) =>
                      comment.id === commentId
                        ? {
                            ...comment,
                            reactions: {
                              ...applyReactionSummaryChange(
                                comment.reactions,
                                reaction,
                                currentUserId,
                              ),
                            },
                          }
                        : comment,
                    ),
                  },
                };
              }
            }
            return post;
          }),
        })),
      };
    },
  );
};

// お気に入り画面のリアクション更新
const updateFavoritePostReaction = (
  queryClient: QueryClient,
  {
    postObjectId,
    commentId,
    reaction,
    targetType,
    currentUserId,
  }: {
    postObjectId: number;
    commentId?: number;
    reaction: ReactionType | null;
    targetType: "post" | "comment";
    currentUserId?: number;
  },
) => {
  const queryKey = cacheKeys.favoritePosts.list();

  queryClient.setQueryData<InfiniteData<FavoritePostsPage>>(
    queryKey,
    (oldData) => {
      if (!oldData?.pages) return oldData;

      return {
        ...oldData,
        pages: oldData.pages.map((page) => ({
          ...page,
          results: page.results.map((favoritePost: FavoritePostResponse) => {
            if (favoritePost.content.metadata.object_id === postObjectId) {
              if (targetType === "post") {
                return {
                  ...favoritePost,
                  content: {
                    ...favoritePost.content,
                    reactions: applyReactionSummaryChange(
                      favoritePost.content.reactions,
                      reaction,
                      currentUserId,
                    ),
                  },
                };
              }
              if (targetType === "comment" && commentId) {
                // コメントのリアクション更新
                return {
                  ...favoritePost,
                  content: {
                    ...favoritePost.content,
                    comments: {
                      ...favoritePost.content.comments,
                      latest: favoritePost.content.comments?.latest?.map(
                        (comment) =>
                          comment.id === commentId
                            ? {
                                ...comment,
                                reactions: {
                                  ...applyReactionSummaryChange(
                                    comment.reactions,
                                    reaction,
                                    currentUserId,
                                  ),
                                },
                              }
                            : comment,
                      ),
                    },
                  },
                };
              }
            }
            return favoritePost;
          }),
        })),
      };
    },
  );
};

// コメントキャッシュの更新
const updateCommentReaction = (
  queryClient: QueryClient,
  {
    postObjectId,
    commentId,
    reaction,
    currentUserId,
  }: {
    postObjectId: number;
    commentId: number;
    reaction: ReactionType | null;
    currentUserId?: number;
  },
) => {
  const queryKey = cacheKeys.comments.post(postObjectId);

  queryClient.setQueryData<CommentsData>(queryKey, (oldData) => {
    if (!oldData?.comments) return oldData;

    return {
      ...oldData,
      comments: oldData.comments.map((comment) =>
        comment.id === commentId
          ? {
              ...comment,
              reactions: {
                ...applyReactionSummaryChange(
                  comment.reactions,
                  reaction,
                  currentUserId,
                ),
              },
            }
          : comment,
      ),
    };
  });
};

// 全キャッシュ無効化（確実に同期を取りたい場合）
export const invalidateAllRelatedCaches = (
  queryClient: QueryClient,
  {
    postObjectId,
    groupId,
  }: {
    postObjectId: number;
    groupId?: number;
  },
) => {
  // お気に入りキャッシュ無効化
  queryClient.invalidateQueries({ queryKey: cacheKeys.favoritePosts.list() });

  // ストリームキャッシュ無効化
  if (groupId) {
    queryClient.invalidateQueries({ queryKey: cacheKeys.posts.list(groupId) });
  } else {
    queryClient.invalidateQueries({ queryKey: cacheKeys.posts.lists() });
  }

  // コメントキャッシュ無効化
  queryClient.invalidateQueries({
    queryKey: cacheKeys.comments.post(postObjectId),
  });
};

export const updateFavoriteStatusInAllCaches = (
  queryClient: QueryClient,
  {
    postObjectId,
    postContentId,
    isFavorited,
    groupId,
  }: {
    postObjectId: number;
    postContentId: number;
    isFavorited: boolean;
    groupId?: number;
  },
) => {
  // 1. ストリーム画面のキャッシュ更新
  if (groupId) {
    updateStreamPostFavorite(queryClient, {
      postObjectId,
      postContentId,
      isFavorited,
      groupId,
    });
  }

  // 2. お気に入り一覧のキャッシュは無効化（追加/削除があるため）
  queryClient.invalidateQueries({ queryKey: cacheKeys.favoritePosts.list() });
};

// ストリーム画面のお気に入り状態更新
const updateStreamPostFavorite = (
  queryClient: QueryClient,
  {
    postObjectId,
    postContentId,
    isFavorited,
    groupId,
  }: {
    postObjectId: number;
    postContentId: number;
    isFavorited: boolean;
    groupId: number;
  },
) => {
  const queryKey = cacheKeys.posts.list(groupId);

  queryClient.setQueryData<InfiniteData<StreamContentPage>>(
    queryKey,
    (oldData) => {
      if (!oldData?.pages) {
        console.log("❌ No stream data found for favorite update");
        return oldData;
      }

      return {
        ...oldData,
        pages: oldData.pages.map((page) => ({
          ...page,
          results: page.results.map((post: StreamContent) => {
            // postContentIdで一致する投稿を更新
            if (
              post.id === postContentId ||
              post.metadata?.object_id === postObjectId
            ) {
              return {
                ...post,
                user_marked_readlater: isFavorited,
              };
            }
            return post;
          }),
        })),
      };
    },
  );
};

export const postsKeys = {
  all: ["posts"] as const,
  list: (groupId: number) => ["posts", "list", groupId] as const,
  detail: (postObjectId: number) => ["posts", "detail", postObjectId] as const,
  comments: (postObjectId: number) =>
    ["posts", "comments", postObjectId] as const,
  commentDetail: (commentId: number) =>
    ["posts", "comment", commentId] as const,
};

// 既存の関数をレガシーサポートとして残す
export const favoritePostsKeys = cacheKeys.favoritePosts;
export const invalidatePostsCache = (
  queryClient: QueryClient,
  groupId?: number,
) => {
  if (groupId) {
    queryClient.invalidateQueries({ queryKey: cacheKeys.posts.list(groupId) });
  } else {
    queryClient.invalidateQueries({ queryKey: cacheKeys.posts.lists() });
  }
};
export const invalidateFavoritePostsCache = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: cacheKeys.favoritePosts.list() });
};
